import { useState, useEffect, useCallback, useRef } from 'react';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  loading: boolean;
  error: string | null;
}

interface UseDataCacheOptions {
  cacheKey: string;
  fetchFunction: () => Promise<any>;
  dependencies?: any[];
  cacheDuration?: number; // in milliseconds, default 5 minutes
  autoRefresh?: boolean; // whether to refresh data when component mounts
  preloadCondition?: () => boolean; // condition for when to preload
}

// Global request deduplication map
const activeRequests = new Map<string, Promise<any>>();

export function useDataCache<T>({
  cacheKey,
  fetchFunction,
  dependencies = [],
  cacheDuration = 5 * 60 * 1000, // 5 minutes default
  autoRefresh = true,
  preloadCondition
}: UseDataCacheOptions) {
  const [cache, setCache] = useState<Map<string, CacheEntry<T>>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Check if cached data is still valid
  const isCacheValid = useCallback((entry: CacheEntry<T>) => {
    const now = Date.now();
    return (now - entry.timestamp) < cacheDuration;
  }, [cacheDuration]);

  // Fetch data and update cache with request deduplication
  const fetchData = useCallback(async (forceRefresh = false) => {
    const cachedEntry = cache.get(cacheKey);
    
    // Return cached data if valid and not forcing refresh
    if (!forceRefresh && cachedEntry && isCacheValid(cachedEntry)) {
      console.log(`📦 Using cached data for ${cacheKey}`);
      console.log(`📊 Cached data sample for ${cacheKey}:`, cachedEntry.data && Array.isArray(cachedEntry.data) && cachedEntry.data.length > 0 ? cachedEntry.data[0] : 'No data');
      return cachedEntry.data;
    }

    // Check if there's already an active request for this cache key
    if (activeRequests.has(cacheKey)) {
      console.log(`🔄 Request already in progress for ${cacheKey}, waiting...`);
      return activeRequests.get(cacheKey);
    }

    // Set loading state
    setIsLoading(true);
    setError(null);

    // Create abort controller for this request
    abortControllerRef.current = new AbortController();

    // Create the request promise
    const requestPromise = (async () => {
      try {
        console.log(`🔄 Fetching data for ${cacheKey}...`);
        const data = await fetchFunction();
        
        // Update cache
        const newEntry: CacheEntry<T> = {
          data,
          timestamp: Date.now(),
          loading: false,
          error: null
        };
        
        setCache(prev => new Map(prev).set(cacheKey, newEntry));
        setIsLoading(false);
        
        console.log(`✅ Data fetched successfully for ${cacheKey}`);
        console.log(`📊 Data sample for ${cacheKey}:`, data && Array.isArray(data) && data.length > 0 ? data[0] : 'No data');
        console.log(`📊 Data length for ${cacheKey}:`, data && Array.isArray(data) ? data.length : 0);
        console.log(`📊 Data keys for ${cacheKey}:`, data && Array.isArray(data) && data.length > 0 ? Object.keys(data[0]) : 'No data');
        return data;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch data';
        setError(errorMessage);
        setIsLoading(false);
        
        // Update cache with error state
        const errorEntry: CacheEntry<T> = {
          data: cachedEntry?.data || [] as any,
          timestamp: Date.now(),
          loading: false,
          error: errorMessage
        };
        
        setCache(prev => new Map(prev).set(cacheKey, errorEntry));
        console.error(`❌ Failed to fetch data for ${cacheKey}:`, errorMessage);
        throw err;
      } finally {
        // Remove from active requests
        activeRequests.delete(cacheKey);
      }
    })();

    // Store the request promise
    activeRequests.set(cacheKey, requestPromise);
    
    return requestPromise;
  }, [cacheKey, fetchFunction, cache, isCacheValid]);

  // Get cached data
  const getCachedData = useCallback(() => {
    const cachedEntry = cache.get(cacheKey);
    if (cachedEntry && isCacheValid(cachedEntry)) {
      return cachedEntry.data;
    }
    // Return null for no cached data - let the component handle the default
    return null;
  }, [cacheKey, cache, isCacheValid]);

  // Preload data if condition is met
  const preloadData = useCallback(async () => {
    if (preloadCondition && preloadCondition()) {
      const cachedData = getCachedData();
      if (!cachedData) {
        try {
          await fetchData();
        } catch (err) {
          console.error(`Failed to preload data for ${cacheKey}:`, err);
        }
      }
    }
  }, [preloadCondition, getCachedData, fetchData, cacheKey]);

  // Auto-refresh on mount if enabled
  useEffect(() => {
    if (autoRefresh) {
      fetchData();
    }
  }, [autoRefresh, ...dependencies]);

  // Preload data when dependencies change
  useEffect(() => {
    preloadData();
  }, [preloadData, ...dependencies]);

          // Cleanup expired cache entries
          useEffect(() => {
            const cleanup = () => {
              setCache(prev => {
                const newCache = new Map(prev);
                const now = Date.now();

                // Use Array.from() to avoid iteration issues
                Array.from(newCache.entries()).forEach(([key, entry]) => {
                  if ((now - entry.timestamp) >= cacheDuration) {
                    newCache.delete(key);
                  }
                });

                return newCache;
              });
            };

            // Cleanup every minute
            const interval = setInterval(cleanup, 60 * 1000);
            return () => clearInterval(interval);
          }, [cacheDuration]);

  // Clear cache for this specific key
  const clearCache = useCallback(() => {
    setCache(prev => {
      const newCache = new Map(prev);
      newCache.delete(cacheKey);
      return newCache;
    });
    console.log(`🗑️ Cleared cache for ${cacheKey}`);
  }, [cacheKey]);

  return {
    data: getCachedData(),
    isLoading,
    error,
    fetchData,
    preloadData,
    clearCache,
    cache
  };
} 